/**
 * Labstep
 *
 * @module hoc/Search
 * @desc Enhances children with Params and ReadOnMount props.
 * Reads On mount + on every searchParams change
 */

import { EntityReadCursorContainer } from 'labstep-web/containers/Entity/Read/Cursor';
import { EntityReadPageContainer } from 'labstep-web/containers/Entity/Read/Page';
import { ParamsContext } from 'labstep-web/contexts/params';
import { filterParams, ParamsHOC } from 'labstep-web/hoc/Params';
import { objectOrFunction } from 'labstep-web/services/react.service';
import isEqual from 'lodash/isEqual';
import pick from 'lodash/pick';
import React from 'react';
import type { ISearchContainerProps, SearchHocParams } from './types';

export class SearchContainer extends React.Component<ISearchContainerProps> {
  componentDidMount() {
    const { read, options } = this.props;

    read(options);
  }

  componentDidUpdate(prevProps: ISearchContainerProps) {
    const { read, options } = this.props;

    if (
      !isEqual(prevProps.params, this.props.params) ||
      !isEqual(prevProps.globalParams, this.props.globalParams)
    ) {
      read(options);
    }
  }

  render() {
    const { children, ...rest } = this.props;

    return <>{children(rest)}</>;
  }
}

export const SearchHOC: React.FC<SearchHocParams> = ({
  historyAction,
  initialParams,
  isCursor,
  usePostFilter,
  ...props
}) => (
  <ParamsHOC
    historyAction={historyAction}
    initialParams={initialParams}
  >
    <ParamsContext.Consumer>
      {(paramsProps) => {
        const { children, entityName } = props;

        const {
          searchParams: { page, ...restSearchParams },
        } = paramsProps;
        const params =
          objectOrFunction(props.params, restSearchParams) || {};
        let finalParams: any = {
          ...params,
          ...restSearchParams,
        };

        if (usePostFilter) {
          finalParams = pick(finalParams, filterParams);
        }

        if (paramsProps.globalParams) {
          finalParams = {
            ...finalParams,
            ...paramsProps.globalParams,
          };
        }

        if (isCursor) {
          return (
            <EntityReadCursorContainer
              entityName={entityName}
              params={finalParams}
              injectFields={paramsProps.injectFields}
            >
              {(readProps) => (
                <SearchContainer
                  {...readProps}
                  {...paramsProps}
                  params={{
                    ...params,
                    ...paramsProps.searchParams,
                  }}
                  children={children}
                />
              )}
            </EntityReadCursorContainer>
          );
        }

        return (
          <EntityReadPageContainer
            entityName={entityName}
            params={finalParams}
            page={page || 1}
            usePostFilter={usePostFilter}
            injectFields={paramsProps.injectFields}
          >
            {(readProps) => (
              <SearchContainer
                {...readProps}
                {...paramsProps}
                page={page}
                params={{
                  ...params,
                  ...paramsProps.searchParams,
                }}
                children={children}
              />
            )}
          </EntityReadPageContainer>
        );
      }}
    </ParamsContext.Consumer>
  </ParamsHOC>
);
